import { Fragment } from "react";
import escapeRegExp from "lodash/escapeRegExp";
import * as styles from "./styles_vanilla.css";

type HighlightTextProps = {
  text: string;
  searchValue: string;
};

/**
 * Wraps every case-insensitive occurrence of `searchValue` in `text` with a
 * `<mark>` styled with `styles.searchHighlight`. Empty/whitespace-only
 * `searchValue` (or empty `text`) renders the text unchanged.
 *
 * Shared across Reporting search UIs (data-package grids, the attribute
 * picker, etc.) so the highlight treatment stays consistent.
 */
export const HighlightText = ({ text, searchValue }: HighlightTextProps) => {
  const trimmed = searchValue.trim();
  if (!trimmed || !text) {
    return <>{text}</>;
  }

  // Splitting on a capturing group yields an alternating sequence of
  // [non-match, match, non-match, match, …], so we can identify matches
  // structurally (odd indices) instead of re-testing each part.
  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, "gi"));

  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className={styles.searchHighlight}>
            {part}
          </mark>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
};
